import {
  DndContext,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DeleteIcon from '@mui/icons-material/DeleteOutlineOutlined';
import DragIcon from '@mui/icons-material/DragIndicatorOutlined';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from 'components/Button/Button';
import IconButton from 'components/IconButton/IconButton';
import RadioGroup from 'components/form/RadioGroup';
import TextField from 'components/form/TextInput';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { shortId } from 'utils';
import Preview from '../previews/MultipleChoice';

const SortableOption = ({ index, data, onOptionChange, onRemove }) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: data.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <Stack
      mb={1}
      direction="row"
      spacing={2}
      alignItems="center"
      style={style}
      index={index}
      {...attributes}
      ref={setNodeRef}
    >
      <TextField
        fullWidth
        margin="none"
        inputProps={{
          autoFocus: true,
        }}
        value={data.value}
        onChange={(e) => onOptionChange(data, e.target.value)}
        placeholder={`Option ${index + 1}`}
      />
      <DragIcon {...listeners} style={{ cursor: 'move' }} />
      <IconButton
        edge="end"
        color="error"
        icon={<DeleteIcon />}
        onClick={() => onRemove(data)}
      />
    </Stack>
  );
};

const MultipleChoiceQuestion = ({ values, onChange }) => {
  const [t] = useTranslation('connectCard');
  const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor));
  const options = values.question_possible_answers;
  const { multipleChoice } = values.question_settings;

  useEffect(() => {
    if (!options.length) {
      onAddOption();
      onTypeChange({ target: { value: 'false' } });
    }
  }, []);

  const onTypeChange = (e) => {
    const { value } = e.target;
    values.question_settings.multipleChoice = value === 'true';
    onChange(values);
  };

  const onAddOption = () => {
    options.push({ id: shortId(), value: '' });
    onChange({ question_possible_answers: options });
  };

  const onRemove = (option) => {
    onChange({
      question_possible_answers: options.filter((o) => o.id !== option.id),
    });
  };

  const onOptionChange = (option, value) => {
    const index = options.findIndex((o) => o.id === option.id);
    options[index].value = value;
    onChange({ question_possible_answers: options });
  };

  function handleDragEnd(event) {
    const { active, over } = event;
    if (active.id !== over.id) {
      const oldIndex = options.findIndex((q) => q.id === active.id);
      const newIndex = options.findIndex((q) => q.id === over.id);
      onChange({
        question_possible_answers: arrayMove(options, oldIndex, newIndex),
      });
    }
  }

  return (
    <>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography fontWeight={500}>
          {t('choose-your-question', 'Choose your options')}
        </Typography>
        <RadioGroup
          row
          value={multipleChoice}
          onChange={onTypeChange}
          margin="none"
          options={[
            { label: 'Single Select', value: false },
            { label: 'Multiple Select', value: true },
          ]}
        />
      </Stack>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        modifiers={[restrictToVerticalAxis]}
        autoScroll
      >
        <SortableContext items={options} strategy={rectSortingStrategy}>
          <Stack>
            {options.map((i, idx) => (
              <SortableOption
                key={i.id}
                data={i}
                index={idx}
                onOptionChange={onOptionChange}
                onRemove={onRemove}
              />
            ))}
          </Stack>
        </SortableContext>
      </DndContext>
      <Box justifyContent="flex-end" display="flex">
        <Button variant="text" onClick={onAddOption}>
          {t('add-another-option', 'Add another option')}
        </Button>
      </Box>
    </>
  );
};

MultipleChoiceQuestion.StepsCount = 3;
MultipleChoiceQuestion.Preview = Preview;
MultipleChoiceQuestion.StepDescription = 'What options would you like to add?';

export default MultipleChoiceQuestion;
